import { useState, useEffect, useCallback, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { usePendingApprovalQueue } from "@/hooks/useBlogPosts";
import {
  CheckCircle2, XCircle, SkipForward, Layers, List, Smartphone,
  MapPin, Bot, FileText, MessageSquare, ChevronLeft, ChevronRight,
  Loader2, Inbox,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const T = {
  navy: "#002868",
  red: "#B22234",
  gold: "#C5A55A",
  green: "#16a34a",
  border: "rgba(27,27,27,0.12)",
  bg: "#FAF9F6",
};
const serif = "'Instrument Serif', Georgia, serif";
const sans = "'Instrument Sans', system-ui, sans-serif";

type ViewMode = "stack" | "swipe" | "list";

type QueueItem = {
  id: string;
  type: "blog" | "social";
  title: string;
  stateCode: string | null;
  contentType: string;
  preview: string;
  createdAt: string;
  aiGenerated: boolean;
  // Blog-specific
  slug?: string;
  brandKey?: string;
  sections?: any[];
  // Social-specific
  platform?: string;
  blogPostId?: string;
};

export function UnifiedApprovalQueue() {
  const { data: queue = [], isLoading, refetch } = usePendingApprovalQueue();
  const queryClient = useQueryClient();
  const [viewMode, setViewMode] = useState<ViewMode>("stack");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [denialId, setDenialId] = useState<string | null>(null);
  const [denialReason, setDenialReason] = useState("");
  const [animDir, setAnimDir] = useState<"left" | "right" | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Keyboard navigation for stack mode
  useEffect(() => {
    if (viewMode !== "stack") return;
    const handler = (e: KeyboardEvent) => {
      if (denialId) return; // Don't navigate while denial dialog is open
      if (e.key === "ArrowRight") {
        e.preventDefault();
        handleApprove(queue[currentIndex]);
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        handleDeny(queue[currentIndex]);
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        handleSkip();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [viewMode, currentIndex, queue, denialId]);

  // Swipe gesture handling (CSS-based, no extra deps)
  const cardRef = useRef<HTMLDivElement>(null);
  const dragStart = useRef<{ x: number; y: number } | null>(null);
  const [dragOffset, setDragOffset] = useState(0);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    if (viewMode !== "swipe") return;
    dragStart.current = { x: e.clientX, y: e.clientY };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, [viewMode]);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragStart.current) return;
    const dx = e.clientX - dragStart.current.x;
    setDragOffset(dx);
  }, []);

  const onPointerUp = useCallback(() => {
    if (!dragStart.current) return;
    const threshold = 120;
    if (dragOffset > threshold) {
      handleApprove(queue[currentIndex]);
    } else if (dragOffset < -threshold) {
      handleDeny(queue[currentIndex]);
    }
    dragStart.current = null;
    setDragOffset(0);
  }, [dragOffset, currentIndex, queue]);

  // Mutations
  const approveMutation = useMutation({
    mutationFn: async (item: QueueItem) => {
      const table = item.type === "blog" ? "blog_posts" : "social_posts";
      const { error } = await supabase
        .from(table as any)
        .update({
          status: "approved",
          approved_by: "admin",
          approved_at: new Date().toISOString(),
        } as any)
        .eq("id", item.id);
      if (error) throw error;

      // If blog post approved, auto-approve linked social posts
      if (item.type === "blog") {
        await supabase
          .from("social_posts" as any)
          .update({
            status: "approved",
            approved_by: "admin (auto)",
            approved_at: new Date().toISOString(),
          } as any)
          .eq("blog_post_id", item.id)
          .eq("status", "pending_approval");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pending_approval_queue"] });
      queryClient.invalidateQueries({ queryKey: ["content_pending_count"] });
      queryClient.invalidateQueries({ queryKey: ["social_pending_count"] });
    },
  });

  const denyMutation = useMutation({
    mutationFn: async ({ item, reason }: { item: QueueItem; reason: string }) => {
      const table = item.type === "blog" ? "blog_posts" : "social_posts";
      const { error } = await supabase
        .from(table as any)
        .update({
          status: "denied",
          denial_reason: reason,
        } as any)
        .eq("id", item.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pending_approval_queue"] });
      queryClient.invalidateQueries({ queryKey: ["content_pending_count"] });
      queryClient.invalidateQueries({ queryKey: ["social_pending_count"] });
    },
  });

  function handleApprove(item: QueueItem) {
    if (!item) return;
    setAnimDir("right");
    setTimeout(() => {
      approveMutation.mutate(item);
      setAnimDir(null);
      if (currentIndex >= queue.length - 1) setCurrentIndex(Math.max(0, queue.length - 2));
    }, 250);
  }

  function handleDeny(item: QueueItem) {
    if (!item) return;
    setDenialId(item.id);
    setDenialReason("");
  }

  function confirmDeny() {
    const item = queue.find(q => q.id === denialId);
    if (!item) return;
    setAnimDir("left");
    setTimeout(() => {
      denyMutation.mutate({ item, reason: denialReason });
      setDenialId(null);
      setDenialReason("");
      setAnimDir(null);
      if (currentIndex >= queue.length - 1) setCurrentIndex(Math.max(0, queue.length - 2));
    }, 250);
  }

  function handleSkip() {
    if (currentIndex < queue.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
  }

  function handleBulkApprove() {
    selectedIds.forEach(id => {
      const item = queue.find(q => q.id === id);
      if (item) approveMutation.mutate(item);
    });
    setSelectedIds(new Set());
  }

  // Empty state
  if (!isLoading && queue.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "80px 24px" }}>
        <Inbox size={48} style={{ color: T.gold, margin: "0 auto 16px" }} />
        <h3 style={{ fontFamily: serif, fontSize: 22, color: T.navy, marginBottom: 8 }}>
          All caught up
        </h3>
        <p style={{ fontFamily: sans, fontSize: 14, color: "rgba(27,27,27,0.62)" }}>
          No content pending approval. Use <code>/generate-state-content</code> to create new content.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div style={{ textAlign: "center", padding: 60 }}>
        <Loader2 size={32} style={{ color: T.navy, animation: "spin 1s linear infinite" }} />
      </div>
    );
  }

  const currentItem = queue[currentIndex];

  return (
    <div>
      {/* Header bar with view mode toggle and queue counter */}
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: 20,
        gap: 12,
        flexWrap: "wrap",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontFamily: sans, fontSize: 13, color: T.navy, fontWeight: 600 }}>
            {queue.length} item{queue.length !== 1 ? "s" : ""} pending
          </span>
          {viewMode === "stack" && queue.length > 0 && (
            <span style={{ fontSize: 12, color: "rgba(27,27,27,0.4)", fontFamily: sans }}>
              — {currentIndex + 1} of {queue.length}
            </span>
          )}
        </div>

        {/* View mode toggle */}
        <div style={{
          display: "flex",
          gap: 2,
          backgroundColor: "rgba(0,0,0,0.05)",
          borderRadius: 8,
          padding: 3,
        }}>
          {([
            { mode: "stack" as ViewMode, icon: <Layers size={14} />, label: "Card Stack" },
            { mode: "swipe" as ViewMode, icon: <Smartphone size={14} />, label: "Swipe" },
            { mode: "list" as ViewMode, icon: <List size={14} />, label: "List" },
          ]).map(({ mode, icon, label }) => (
            <button
              key={mode}
              onClick={() => { setViewMode(mode); setCurrentIndex(0); }}
              title={label}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                padding: "5px 12px",
                border: "none",
                borderRadius: 6,
                backgroundColor: viewMode === mode ? "#fff" : "transparent",
                color: viewMode === mode ? T.navy : "rgba(27,27,27,0.45)",
                fontSize: 12,
                fontFamily: sans,
                fontWeight: viewMode === mode ? 600 : 400,
                cursor: "pointer",
                boxShadow: viewMode === mode ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                transition: "all 0.15s",
              }}
            >
              {icon} {label}
            </button>
          ))}
        </div>
      </div>

      {/* ═══ CARD STACK MODE ═══ */}
      {(viewMode === "stack" || viewMode === "swipe") && currentItem && (
        <div style={{ maxWidth: 640, margin: "0 auto" }}>
          {/* Card */}
          <div
            ref={cardRef}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            style={{
              backgroundColor: "#fff",
              border: `1px solid ${T.border}`,
              borderRadius: 12,
              padding: 28,
              boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
              transition: animDir ? "transform 0.25s ease-out, opacity 0.25s ease-out" : "none",
              transform: animDir === "right"
                ? "translateX(120%) rotate(8deg)"
                : animDir === "left"
                ? "translateX(-120%) rotate(-8deg)"
                : viewMode === "swipe"
                ? `translateX(${dragOffset}px) rotate(${dragOffset * 0.04}deg)`
                : "none",
              opacity: animDir ? 0 : 1,
              touchAction: viewMode === "swipe" ? "none" : undefined,
              userSelect: "none",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* Swipe indicators */}
            {viewMode === "swipe" && dragOffset !== 0 && (
              <>
                {dragOffset > 40 && (
                  <div style={{
                    position: "absolute", top: 16, right: 16,
                    backgroundColor: T.green, color: "#fff",
                    padding: "4px 12px", borderRadius: 6, fontSize: 13, fontWeight: 700,
                    fontFamily: sans, opacity: Math.min(1, (dragOffset - 40) / 80),
                  }}>
                    APPROVE
                  </div>
                )}
                {dragOffset < -40 && (
                  <div style={{
                    position: "absolute", top: 16, left: 16,
                    backgroundColor: T.red, color: "#fff",
                    padding: "4px 12px", borderRadius: 6, fontSize: 13, fontWeight: 700,
                    fontFamily: sans, opacity: Math.min(1, (-dragOffset - 40) / 80),
                  }}>
                    DENY
                  </div>
                )}
              </>
            )}

            {/* Type badge */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
              <span style={{
                display: "inline-flex", alignItems: "center", gap: 4,
                padding: "3px 10px", borderRadius: 6, fontSize: 11, fontWeight: 700,
                fontFamily: sans, letterSpacing: "0.05em", textTransform: "uppercase",
                backgroundColor: currentItem.type === "blog" ? "rgba(197,165,90,0.15)" : "rgba(0,40,104,0.1)",
                color: currentItem.type === "blog" ? "#8B7730" : T.navy,
              }}>
                {currentItem.type === "blog" ? <FileText size={12} /> : <MessageSquare size={12} />}
                {currentItem.type === "blog" ? "Blog Post" : `Social · ${currentItem.platform}`}
              </span>

              {currentItem.stateCode && (
                <span style={{
                  display: "inline-flex", alignItems: "center", gap: 3,
                  padding: "3px 8px", borderRadius: 6, fontSize: 11, fontWeight: 600,
                  backgroundColor: "rgba(197,165,90,0.12)", color: "#8B7730",
                  fontFamily: sans,
                }}>
                  <MapPin size={10} /> {currentItem.stateCode}
                </span>
              )}

              {currentItem.aiGenerated && (
                <span style={{
                  display: "inline-flex", alignItems: "center", gap: 3,
                  padding: "3px 8px", borderRadius: 6, fontSize: 11, fontWeight: 600,
                  backgroundColor: "rgba(147,51,234,0.1)", color: "#7c3aed",
                  fontFamily: sans,
                }}>
                  <Bot size={10} /> AI
                </span>
              )}
            </div>

            {/* Title */}
            <h3 style={{ fontFamily: serif, fontSize: 22, color: T.navy, marginBottom: 12, lineHeight: 1.3 }}>
              {currentItem.title}
            </h3>

            {/* Preview content */}
            {currentItem.type === "blog" && currentItem.sections?.[0] && (
              <div
                style={{
                  fontFamily: sans, fontSize: 14, color: "#555", lineHeight: 1.6,
                  maxHeight: 200, overflow: "hidden",
                  maskImage: "linear-gradient(to bottom, black 70%, transparent 100%)",
                  WebkitMaskImage: "linear-gradient(to bottom, black 70%, transparent 100%)",
                }}
                dangerouslySetInnerHTML={{ __html: currentItem.sections[0].content }}
              />
            )}
            {currentItem.type === "social" && (
              <p style={{
                fontFamily: sans, fontSize: 15, color: "#333", lineHeight: 1.5,
                whiteSpace: "pre-wrap",
              }}>
                {currentItem.preview}
              </p>
            )}

            {/* Metadata */}
            <div style={{
              marginTop: 16, paddingTop: 12, borderTop: `1px solid ${T.border}`,
              display: "flex", gap: 16, fontSize: 12, color: "rgba(27,27,27,0.45)", fontFamily: sans,
            }}>
              <span>{new Date(currentItem.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>
              <span>{currentItem.contentType}</span>
            </div>
          </div>

          {/* Action buttons */}
          <div style={{
            display: "flex", justifyContent: "center", gap: 16, marginTop: 24,
          }}>
            <button
              onClick={() => handleDeny(currentItem)}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                padding: "10px 28px", border: `2px solid ${T.red}`, borderRadius: 10,
                backgroundColor: "rgba(178,34,52,0.05)", color: T.red,
                fontSize: 14, fontWeight: 600, fontFamily: sans, cursor: "pointer",
                transition: "all 0.15s",
              }}
            >
              <XCircle size={18} /> Deny
              {viewMode === "stack" && <span style={{ fontSize: 11, opacity: 0.6, marginLeft: 4 }}>←</span>}
            </button>
            <button
              onClick={handleSkip}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                padding: "10px 20px", border: `1px solid ${T.border}`, borderRadius: 10,
                backgroundColor: "#fff", color: "rgba(27,27,27,0.62)",
                fontSize: 14, fontWeight: 500, fontFamily: sans, cursor: "pointer",
              }}
            >
              <SkipForward size={16} /> Skip
              {viewMode === "stack" && <span style={{ fontSize: 11, opacity: 0.6, marginLeft: 4 }}>↓</span>}
            </button>
            <button
              onClick={() => handleApprove(currentItem)}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                padding: "10px 28px", border: `2px solid ${T.green}`, borderRadius: 10,
                backgroundColor: "rgba(22,163,74,0.05)", color: T.green,
                fontSize: 14, fontWeight: 600, fontFamily: sans, cursor: "pointer",
                transition: "all 0.15s",
              }}
            >
              <CheckCircle2 size={18} /> Approve
              {viewMode === "stack" && <span style={{ fontSize: 11, opacity: 0.6, marginLeft: 4 }}>→</span>}
            </button>
          </div>

          {/* Navigation for stack mode */}
          {viewMode === "stack" && queue.length > 1 && (
            <div style={{
              display: "flex", justifyContent: "center", gap: 8, marginTop: 16,
            }}>
              <button
                onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))}
                disabled={currentIndex === 0}
                style={{
                  padding: "4px 8px", border: "none", background: "none",
                  color: currentIndex === 0 ? "rgba(27,27,27,0.2)" : T.navy,
                  cursor: currentIndex === 0 ? "default" : "pointer",
                }}
              >
                <ChevronLeft size={18} />
              </button>
              {/* Dot indicators */}
              {queue.slice(0, 10).map((_, i) => (
                <span
                  key={i}
                  style={{
                    width: 6, height: 6, borderRadius: "50%",
                    backgroundColor: i === currentIndex ? T.navy : "rgba(27,27,27,0.15)",
                    display: "inline-block",
                    cursor: "pointer",
                  }}
                  onClick={() => setCurrentIndex(i)}
                />
              ))}
              {queue.length > 10 && (
                <span style={{ fontSize: 10, color: "rgba(27,27,27,0.4)", fontFamily: sans }}>
                  +{queue.length - 10}
                </span>
              )}
              <button
                onClick={() => setCurrentIndex(Math.min(queue.length - 1, currentIndex + 1))}
                disabled={currentIndex >= queue.length - 1}
                style={{
                  padding: "4px 8px", border: "none", background: "none",
                  color: currentIndex >= queue.length - 1 ? "rgba(27,27,27,0.2)" : T.navy,
                  cursor: currentIndex >= queue.length - 1 ? "default" : "pointer",
                }}
              >
                <ChevronRight size={18} />
              </button>
            </div>
          )}

          {/* Keyboard hint */}
          {viewMode === "stack" && (
            <p style={{
              textAlign: "center", marginTop: 16, fontSize: 11,
              color: "rgba(27,27,27,0.35)", fontFamily: sans,
            }}>
              Keyboard: ← deny · → approve · ↓ skip
            </p>
          )}
          {viewMode === "swipe" && (
            <p style={{
              textAlign: "center", marginTop: 16, fontSize: 11,
              color: "rgba(27,27,27,0.35)", fontFamily: sans,
            }}>
              Swipe right to approve · left to deny
            </p>
          )}
        </div>
      )}

      {/* ═══ LIST MODE ═══ */}
      {viewMode === "list" && (
        <div>
          {/* Bulk actions bar */}
          {selectedIds.size > 0 && (
            <div style={{
              display: "flex", alignItems: "center", gap: 12, marginBottom: 16,
              padding: "10px 16px", backgroundColor: "rgba(0,40,104,0.05)",
              borderRadius: 8, border: `1px solid ${T.border}`,
            }}>
              <span style={{ fontFamily: sans, fontSize: 13, fontWeight: 600, color: T.navy }}>
                {selectedIds.size} selected
              </span>
              <Button size="sm" variant="outline" onClick={handleBulkApprove}
                style={{ color: T.green, borderColor: T.green }}>
                <CheckCircle2 size={14} /> Approve All
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())}
                style={{ fontSize: 12 }}>
                Clear
              </Button>
            </div>
          )}

          {/* List items */}
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {queue.map(item => {
              const isSelected = selectedIds.has(item.id);
              return (
                <div
                  key={item.id}
                  style={{
                    display: "flex", alignItems: "center", gap: 12,
                    padding: "12px 16px",
                    backgroundColor: isSelected ? "rgba(0,40,104,0.04)" : "#fff",
                    border: `1px solid ${isSelected ? "rgba(0,40,104,0.2)" : T.border}`,
                    borderRadius: 8,
                    transition: "all 0.1s",
                  }}
                >
                  {/* Checkbox */}
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => {
                      const next = new Set(selectedIds);
                      if (isSelected) next.delete(item.id);
                      else next.add(item.id);
                      setSelectedIds(next);
                    }}
                    style={{ width: 16, height: 16, cursor: "pointer" }}
                  />

                  {/* Type badge */}
                  <span style={{
                    display: "inline-flex", alignItems: "center", gap: 3,
                    padding: "2px 8px", borderRadius: 4, fontSize: 10, fontWeight: 700,
                    fontFamily: sans, textTransform: "uppercase",
                    backgroundColor: item.type === "blog" ? "rgba(197,165,90,0.15)" : "rgba(0,40,104,0.08)",
                    color: item.type === "blog" ? "#8B7730" : T.navy,
                    flexShrink: 0,
                  }}>
                    {item.type === "blog" ? <FileText size={10} /> : <MessageSquare size={10} />}
                    {item.type === "blog" ? "Blog" : item.platform}
                  </span>

                  {/* State badge */}
                  {item.stateCode && (
                    <span style={{
                      fontSize: 10, fontWeight: 600, color: "#8B7730",
                      backgroundColor: "rgba(197,165,90,0.12)",
                      padding: "2px 6px", borderRadius: 4, fontFamily: sans,
                      flexShrink: 0,
                    }}>
                      {item.stateCode}
                    </span>
                  )}

                  {/* Title */}
                  <span style={{
                    flex: 1, fontFamily: sans, fontSize: 13, fontWeight: 500,
                    color: T.navy, overflow: "hidden", textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}>
                    {item.title}
                  </span>

                  {/* AI badge */}
                  {item.aiGenerated && (
                    <Bot size={12} style={{ color: "#7c3aed", flexShrink: 0 }} />
                  )}

                  {/* Inline actions */}
                  <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                    <button
                      onClick={() => handleDeny(item)}
                      style={{
                        padding: "4px 10px", border: `1px solid rgba(178,34,52,0.3)`,
                        borderRadius: 6, backgroundColor: "transparent",
                        color: T.red, fontSize: 11, fontWeight: 600,
                        fontFamily: sans, cursor: "pointer",
                      }}
                    >
                      Deny
                    </button>
                    <button
                      onClick={() => handleApprove(item)}
                      style={{
                        padding: "4px 10px", border: `1px solid rgba(22,163,74,0.3)`,
                        borderRadius: 6, backgroundColor: "rgba(22,163,74,0.05)",
                        color: T.green, fontSize: 11, fontWeight: 600,
                        fontFamily: sans, cursor: "pointer",
                      }}
                    >
                      Approve
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ═══ DENIAL REASON DIALOG ═══ */}
      {denialId && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 999,
          display: "flex", alignItems: "center", justifyContent: "center",
          backgroundColor: "rgba(0,0,0,0.4)",
        }}>
          <div style={{
            backgroundColor: "#fff", borderRadius: 12, padding: 28,
            width: 420, maxWidth: "90vw", boxShadow: "0 16px 48px rgba(0,0,0,0.2)",
          }}>
            <h3 style={{ fontFamily: serif, fontSize: 20, color: T.navy, marginBottom: 16 }}>
              Deny Content
            </h3>
            <textarea
              value={denialReason}
              onChange={e => setDenialReason(e.target.value)}
              placeholder="Reason for denial (optional)..."
              rows={3}
              style={{
                width: "100%", padding: 12, borderRadius: 8,
                border: `1px solid ${T.border}`, fontFamily: sans,
                fontSize: 14, resize: "vertical", boxSizing: "border-box",
              }}
              autoFocus
            />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
              <Button variant="ghost" onClick={() => setDenialId(null)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={confirmDeny}
                style={{ backgroundColor: T.red }}
              >
                Deny Content
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
